// src/pages/SendWebinarEmails.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";

/* API base */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000"; // fallback for dev

/* Theme */
const theme = {
  brand: "#3dccc7",
  accent: "#ff914d",
  white: "#f1f1f1",
  subtext: "#d6d9de",
  card: "#0f1b2d",
  border: "#1e293b",
  bg: "#0b1320",
};

/* Styled (same as your original — trimmed to save space) */
const Wrap = styled.div`
  display: grid;
  gap: 20px;
`;
const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: ${theme.white};
`;
const Card = styled.div`
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 20px;
`;
const Label = styled.label`
  font-size: 13px;
  color: ${theme.subtext};
  display: block;
  margin-bottom: 4px;
`;
const Input = styled.input`
  width: 100%;
  background: ${theme.bg};
  color: ${theme.white};
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 12px;
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  background: ${theme.bg};
  color: ${theme.white};
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 12px;
`;
const Button = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid ${theme.border};
  background: ${theme.accent};
  color: #111;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    filter: brightness(1.05);
  }
  &[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const EmailList = styled.div`
  margin-bottom: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  font-size: 14px;
  color: ${theme.white};
`;
const RecipientBadge = styled.span`
  background: ${theme.accent};
  color: #111;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  margin-right: 4px;
  display: inline-block;
  margin-bottom: 4px;
  position: relative;
  cursor: pointer;
  &:hover::after {
    content: attr(data-email);
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background: ${theme.white};
    color: #111;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }
`;
const TableShell = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 16px;
  overflow: hidden;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Thead = styled.thead`
  background: #0b1626;
`;
const Th = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  color: ${theme.subtext};
  border-bottom: 1px solid ${theme.border};
`;
const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: ${theme.white};
  border-bottom: 1px solid ${theme.border};
`;
const ClickableTd = styled(Td)`
  color: ${theme.accent};
  cursor: pointer;
  text-decoration: underline;
`;
const Tr = styled.tr`
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: grid;
  place-items: center;
  z-index: 50;
`;
const Modal = styled.div`
  width: min(800px, 92vw);
  max-height: 80vh;
  border-radius: 16px;
  background: ${theme.card};
  border: 1px solid ${theme.border};
  padding: 20px;
  overflow-y: auto;
`;
const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${theme.white};
  font-size: 20px;
  float: right;
  cursor: pointer;
`;

export default function SendWebinarEmails() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [emails, setEmails] = useState([]);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]); // from backend
  const [viewMail, setViewMail] = useState(null); // modal data for a campaign
  const [viewLoading, setViewLoading] = useState(false);

  const fileInputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);

      const extracted = data
        .map((row) => ({
          firstName: row["First Name"] || "",
          lastName: row["Last Name"] || "",
          email: row["Email"] || "",
        }))
        .filter((x) => typeof x.email === "string" && x.email.includes("@"));

      setEmails(extracted);
    };
    reader.readAsBinaryString(file);
  };

  async function loadHistory() {
    try {
      const r = await fetch(`${API_BASE}/api/webinar-emails/campaigns`, { credentials: "include" });
      const j = await r.json();
      if (j?.ok) setHistory(j.data);
    } catch (e) {
      console.error("history load failed:", e);
    }
  }

  useEffect(() => {
    loadHistory();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !body || emails.length === 0) return;
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/api/webinar-emails/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, body, recipients: emails }),
        credentials: "include",
      });
      const j = await r.json();
      if (!j?.ok) {
        alert(j?.error || "Failed to create campaign");
      } else {
        // refresh list
        await loadHistory();

        // Clear form
        setSubject("");
        setBody("");
        setEmails([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        alert(j.message || "Campaign created!");
      }
    } catch (err) {
      console.error(err);
      alert("Network error while creating campaign.");
    } finally {
      setSending(false);
    }
  };

  const openModal = async (campaignId) => {
    try {
      setViewLoading(true);
      const r = await fetch(
        `${API_BASE}/api/webinar-emails/campaigns/${campaignId}?limit=500`,
        { credentials: "include" }
      );
      const j = await r.json();
      if (j?.ok) {
        setViewMail(j.data);
      } else {
        alert(j?.error || "Unable to load campaign details.");
      }
    } catch (e) {
      console.error(e);
      alert("Network error while loading details.");
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <Wrap>
      <Title>Send Webinar Emails</Title>

      <Card>
        <form onSubmit={handleSubmit}>
          <div>
            <Label>Upload Excel File</Label>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFile}
              ref={fileInputRef}
            />
          </div>

          {emails.length > 0 && (
            <EmailList>
              <strong>Recipients ({emails.length}):</strong>
              <div>
                {emails.map((r, i) => (
                  <RecipientBadge key={i} data-email={r.email}>
                    {r.firstName} {r.lastName}
                  </RecipientBadge>
                ))}
              </div>
            </EmailList>
          )}

          <div>
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          <div>
            <Label>
              Mail Body (supports HTML; you can use {"{{name}}"} placeholder)
            </Label>
            <TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Hi {{name}},\n\nThis is a reminder for our webinar...`}
              required
            />
          </div>

          <Button type="submit" disabled={sending}>
            {sending ? "Creating Campaign..." : "Send Emails"}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 style={{ color: theme.white, marginTop: 0 }}>History</h3>
        <TableShell>
          <Table>
            <Thead>
              <tr>
                <Th>ID</Th>
                <Th>Subject</Th>
                <Th>Date</Th>
                <Th>Status</Th>
                <Th>Sent</Th>
                <Th>Failed</Th>
                <Th>Recipients</Th>
              </tr>
            </Thead>
            <tbody>
              {history.map((c, idx) => (
                <Tr key={c.id}>
                  <Td>{idx + 1}</Td>
                  <Td>{c.subject}</Td>
                  <Td>{new Date(c.createdAt).toISOString().split("T")[0]}</Td>
                  <Td>{c.status}</Td>
                  <Td>{c.stats?.sent ?? 0}</Td>
                  <Td>{c.stats?.failed ?? 0}</Td>
                  <ClickableTd onClick={() => openModal(c.id)}>
                    {c.stats?.total ?? 0}
                  </ClickableTd>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableShell>
      </Card>

      {viewMail && (
        <Overlay>
          <Modal>
            <CloseBtn onClick={() => setViewMail(null)}>✕</CloseBtn>
            <h3 style={{ color: theme.white, marginTop: 0 }}>
              {viewMail.subject}
            </h3>
            <p
              style={{
                color: theme.subtext,
                fontSize: "13px",
                marginBottom: "8px",
              }}
            >
              Created: {new Date(viewMail.createdAt).toLocaleString()}
              {viewMail.startedAt
                ? ` • Started: ${new Date(viewMail.startedAt).toLocaleString()}`
                : ""}
              {viewMail.completedAt
                ? ` • Completed: ${new Date(
                    viewMail.completedAt
                  ).toLocaleString()}`
                : ""}
              {viewLoading ? " • Loading..." : ""}
            </p>
            <div
              style={{
                color: theme.white,
                whiteSpace: "pre-wrap",
                marginBottom: "16px",
                maxHeight: "200px",
                overflowY: "auto",
                paddingRight: "4px",
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: 10,
                background: theme.bg,
              }}
              dangerouslySetInnerHTML={{ __html: viewMail.body }}
            />
            <div>
              <strong style={{ color: theme.subtext }}>Recipients:</strong>
              <div style={{ marginTop: "8px" }}>
                {viewMail.recipients.map((r) => (
                  <RecipientBadge key={r.id} data-email={r.email}>
                    {r.firstName} {r.lastName} — {r.status}
                  </RecipientBadge>
                ))}
              </div>
            </div>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
}
